import React, { useEffect, useMemo, useRef, useState } from "react";
import { useAtomValue } from "jotai";
import {
  type CompanyData,
  InputKey,
  type InputValue,
  type TestSuiteRunRecordingData,
} from "../../utils/types";
import { playbackTimestampAtomFromRunId } from "../../atoms/playback-timestamp-atom";
import "react-virtualized/styles.css";
import { Tab } from "@mui/material";
import styled from "@emotion/styled";
import { TabContext, TabList } from "@mui/lab";
import InteractionLogsTab from "./interactionLogsTab";
import NetworkTab from "./networkLogsTab";
import ConsoleTab from "./consoleLogsTab";
import { getInputValues } from "../../utils/helpers";
import CodeGenerationTab from "./codeGenerationTab";
import SessionInfoTab from "./sessionInfoTab";
import ReviewIterationsTab from "./reviewIterationsTab";

const SessionInfoTabs = ({
  runRecordingData,
  companyData,
}: {
  runRecordingData: TestSuiteRunRecordingData;
  companyData?: CompanyData;
}) => {
  const parentRun = runRecordingData.runs.at(-1)!;
  const playbackTimestampAtom = playbackTimestampAtomFromRunId(parentRun.id!);
  const playbackTimestamp = useAtomValue(playbackTimestampAtom);

  const [value, setValue] = React.useState("0");

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  const testInputs = useMemo(() => {
    const runs = runRecordingData.runs;
    return runs.reduce<InputValue[]>((prev, current, index) => {
      const inputValues = getInputValues({
        currentRun: current,
        nextRunsToExecute: runs.slice(index + 1),
        envVars: [],
      });

      return [...prev, ...inputValues];
    }, []);
  }, [runRecordingData]);

  const preRequestRememberedKeys = useMemo(
    () =>
      Array.from(
        new Set(
          runRecordingData.runs.flatMap(
            (run) =>
              run.settings?.preRequests?.flatMap(
                (preRequest) => preRequest.valuesToRemember ?? []
              ) ?? []
          )
        )
      ),
    [runRecordingData.runs]
  );

  const [showScrollButtons, setShowScrollButtons] = useState(false);
  const tabsContainerRef = useRef(null);

  useEffect(() => {
    const checkForOverflow = () => {
      if (tabsContainerRef.current) {
        const { scrollWidth, clientWidth } = tabsContainerRef.current;
        setShowScrollButtons(scrollWidth > clientWidth);
      }
    };

    // Run once on mount
    checkForOverflow();

    // Also run on resize
    window.addEventListener("resize", checkForOverflow);
    return () => {
      window.removeEventListener("resize", checkForOverflow);
    };
  }, []);

  const reviewIterations = parentRun.runData?.reviewIterations ?? [];
  const failureCodeReview = parentRun.runData?.failureCodeReview;
  const showAutoReviewTab =
    reviewIterations.length > 0 || failureCodeReview != null;

  return (
    <TabContext value={value}>
      <TabSection>
        <TabList
          ref={tabsContainerRef}
          onChange={handleChange}
          aria-label="Logs Tabs"
          centered
          // variant={showScrollButtons ? "scrollable" : "standard"}
        >
          <Tab value={"info"} label="Session Info" />
          <Tab value={"0"} label="Interaction" />
          <Tab value={"1"} label="Network" />
          <Tab value={"2"} label="Console" />
          {companyData?.settings.enableCodeGeneration && (
            <Tab value={"3"} label="Code" />
          )}
          {showAutoReviewTab && (
            <Tab value={"4"} label="Auto-review" />
          )}
        </TabList>
      </TabSection>
      <SessionInfoTab
        display={value === "info"}
        runRecordingData={runRecordingData}
      />
      <InteractionLogsTab
        setTabValue={setValue}
        interactionLogs={runRecordingData?.recording.interactionLogs ?? []}
        playbackTimestamp={playbackTimestamp}
        display={value === "0"}
        testInputs={testInputs}
        companyData={companyData}
        preRequestRememberedKeys={preRequestRememberedKeys}
        deviceScreen={runRecordingData.runs.at(-1)?.runData?.dimensions}
      />
      <NetworkTab
        playbackTimestamp={playbackTimestamp}
        networkLogs={runRecordingData?.recording.networkLogs ?? []}
        display={value === "1"}
        startedTimeInEpoch={parentRun.startedTimeInEpoch}
      />
      <ConsoleTab
        consoleLogs={runRecordingData?.recording.logs ?? []}
        playbackTimestamp={playbackTimestamp}
        display={value === "2"}
        logsUrl={parentRun.runData.tempDeviceLogsUrl}
        platform={parentRun.platform}
        startedTimeInEpoch={parentRun.startedTimeInEpoch}
      />
      <CodeGenerationTab
        display={value === "3"}
        generatedCode={runRecordingData?.runs?.at(-1)?.runData?.generatedCode}
      />
      {showAutoReviewTab && (
        <ReviewIterationsTab
          display={value === "4"}
          iterations={reviewIterations}
          finalClassification={parentRun.runData?.finalFailureClassification}
          reviewSystemError={parentRun.runData?.reviewSystemError}
          failureCodeReview={failureCodeReview}
        />
      )}
    </TabContext>
  );
};

const TabSection = styled.div`
  grid-area: tabs;
  border-bottom: 1px solid rgba(255, 255, 255, 0.12);
  margin-inline: 16px;
`;

export function getNextReplayFrameIndex({
  frames,
  targetOffsetMs,
  allowExact = false,
}: {
  frames: any[];
  targetOffsetMs: number;
  allowExact?: boolean;
}): number | undefined {
  return frames.reduce<number | undefined>((foundIndex, item, currentIndex) => {
    if (
      item.relativeTimestamp < targetOffsetMs ||
      (!allowExact && item.relativeTimestamp === targetOffsetMs)
    ) {
      return foundIndex;
    }
    if (
      (allowExact && item.relativeTimestamp === targetOffsetMs) ||
      foundIndex === undefined ||
      item.relativeTimestamp < frames[foundIndex].relativeTimestamp
    ) {
      return currentIndex;
    }
    return foundIndex;
  }, undefined);
}

export { SessionInfoTabs };
